import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getFeedbackSummary, listFeedback } from '~/api/feedback'
import { getMessage } from '~/api/sessions'
import type { AdminFeedback, FeedbackSummary, MessageDetail } from '~/api/types'
import { StatCard } from '~/components/StatCard'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { SearchInput } from '~/components/ui/search-input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { useToast } from '~/components/ui/toast'
import { cn, formatDateTime } from '~/lib/utils'

// 서비스 피드백 탭. 요약 카드(전체/👍/👎/긍정률) + 목록 테이블(rating 배지·코멘트 등).
// 메시지 대상 행은 클릭 시 평가된 답변·소속 세션을 드릴다운한다.
const PAGE_SIZE = 20

/** 피드백 작성자 표시명: 이름 > 이메일 > '익명'. raw uuid는 노출하지 않는다. */
function feedbackUser(f: AdminFeedback): string {
    return f.userName || f.userEmail || '익명'
}

export function FeedbackPanel({ service }: { service: string }) {
    const toast = useToast()
    const [summary, setSummary] = useState<FeedbackSummary | null>(null)
    const [items, setItems] = useState<AdminFeedback[]>([])
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [q, setQ] = useState('')
    const [offset, setOffset] = useState(0)
    // 드릴다운: 클릭한 피드백 + 역추적한 평가 대상 메시지(소속 세션 포함).
    const [detail, setDetail] = useState<{
        feedback: AdminFeedback
        message: MessageDetail
    } | null>(null)

    useEffect(() => {
        let alive = true
        getFeedbackSummary(service)
            .then((data) => {
                if (alive) setSummary(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
        return () => {
            alive = false
        }
    }, [service])

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        listFeedback(service, {
            q: q.trim() || undefined,
            limit: PAGE_SIZE,
            offset,
        })
            .then((data) => {
                if (alive) setItems(data)
            })
            .catch((e) => {
                if (alive) setError(e.message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [service, q, offset])

    // 메시지 대상만 드릴다운 가능(target_id = dialog_messages.id).
    function drillable(f: AdminFeedback): boolean {
        return f.targetType === 'message' && Boolean(f.targetId)
    }

    async function openDetail(f: AdminFeedback) {
        if (!drillable(f) || !f.targetId) return
        try {
            const message = await getMessage(f.targetId)
            setDetail({ feedback: f, message })
        } catch (e) {
            toast.error(
                (e as Error).message || '평가된 메시지를 찾을 수 없어요.'
            )
        }
    }

    const upRate =
        summary && summary.total > 0
            ? `${Math.round((summary.up / summary.total) * 100)}%`
            : '-'

    return (
        <div>
            {error ? <p className='text-destructive mb-4'>{error}</p> : null}

            <div className='mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4'>
                <StatCard label='전체' value={summary?.total ?? '-'} />
                <StatCard label='👍 긍정' value={summary?.up ?? '-'} />
                <StatCard label='👎 부정' value={summary?.down ?? '-'} />
                <StatCard label='긍정률' value={upRate} />
            </div>

            <div className='mb-4'>
                <SearchInput
                    className='w-72'
                    value={q}
                    onChange={(v) => {
                        setOffset(0)
                        setQ(v)
                    }}
                    placeholder='코멘트·사용자 검색'
                />
            </div>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>평가</TableHead>
                        <TableHead>대상</TableHead>
                        <TableHead>코멘트</TableHead>
                        <TableHead>사용자</TableHead>
                        <TableHead>생성일</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell
                                colSpan={5}
                                className='text-muted-foreground py-8 text-center'>
                                불러오는 중…
                            </TableCell>
                        </TableRow>
                    ) : items.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={5}
                                className='text-muted-foreground py-8 text-center'>
                                피드백이 없어요.
                            </TableCell>
                        </TableRow>
                    ) : (
                        items.map((f) => {
                            const canDrill = drillable(f)
                            return (
                                <TableRow
                                    key={f.id}
                                    onClick={
                                        canDrill
                                            ? () => openDetail(f)
                                            : undefined
                                    }
                                    className={cn(
                                        canDrill &&
                                            'hover:bg-accent cursor-pointer'
                                    )}>
                                    <TableCell>
                                        <Badge
                                            tone={
                                                f.rating > 0
                                                    ? 'success'
                                                    : 'danger'
                                            }>
                                            {f.rating > 0 ? '👍' : '👎'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className='whitespace-nowrap'>
                                        <span className='inline-flex items-center gap-1.5'>
                                            {f.targetType}
                                            {canDrill ? (
                                                <span className='text-primary text-xs'>
                                                    대화 보기 →
                                                </span>
                                            ) : null}
                                        </span>
                                    </TableCell>
                                    <TableCell className='max-w-md truncate'>
                                        {f.comment ?? '-'}
                                    </TableCell>
                                    <TableCell
                                        className='max-w-[180px] truncate'
                                        title={
                                            f.userEmail ?? f.userId ?? undefined
                                        }>
                                        {feedbackUser(f)}
                                    </TableCell>
                                    <TableCell className='whitespace-nowrap'>
                                        {formatDateTime(f.createdAt)}
                                    </TableCell>
                                </TableRow>
                            )
                        })
                    )}
                </TableBody>
            </Table>

            <div className='mt-4 flex items-center justify-between'>
                <span className='text-muted-foreground text-xs'>
                    {offset + 1}–{offset + items.length}
                </span>
                <div className='flex gap-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={offset === 0}
                        onClick={() =>
                            setOffset((o) => Math.max(0, o - PAGE_SIZE))
                        }>
                        이전
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={items.length < PAGE_SIZE}
                        onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                        다음
                    </Button>
                </div>
            </div>

            {detail ? (
                <FeedbackDetailPanel
                    feedback={detail.feedback}
                    message={detail.message}
                    userLabel={feedbackUser(detail.feedback)}
                    onClose={() => setDetail(null)}
                />
            ) : null}
        </div>
    )
}

// 피드백 드릴다운 슬라이드오버. 평가된 답변(assistant 메시지) 전문 + 소속 세션으로 이동.
function FeedbackDetailPanel({
    feedback,
    message,
    userLabel,
    onClose,
}: {
    feedback: AdminFeedback
    message: MessageDetail
    userLabel: string
    onClose: () => void
}) {
    const navigate = useNavigate()

    function openConversation() {
        onClose()
        navigate(`/sessions/${message.sessionId}?focus=${message.id}`)
    }

    return (
        <div className='fixed inset-0 z-50 flex justify-end'>
            <button
                type='button'
                aria-label='닫기'
                className='bg-background/60 absolute inset-0'
                onClick={onClose}
            />
            <div className='border-border bg-card relative flex h-full w-full max-w-xl flex-col border-l shadow-lg'>
                <div className='border-border flex items-center justify-between border-b p-4'>
                    <h2 className='krds-title text-foreground'>피드백 상세</h2>
                    <div className='flex items-center gap-2'>
                        <Button
                            variant='outline'
                            size='sm'
                            onClick={openConversation}>
                            대화 보기
                        </Button>
                        <Button variant='ghost' size='sm' onClick={onClose}>
                            닫기
                        </Button>
                    </div>
                </div>

                <div className='flex-1 space-y-4 overflow-y-auto p-4'>
                    <div className='flex flex-wrap items-center gap-2 text-sm'>
                        <Badge
                            tone={feedback.rating > 0 ? 'success' : 'danger'}>
                            {feedback.rating > 0 ? '👍 긍정' : '👎 부정'}
                        </Badge>
                        <span className='text-muted-foreground'>
                            {userLabel} · {formatDateTime(feedback.createdAt)}
                        </span>
                    </div>

                    {feedback.comment ? (
                        <div>
                            <div className='krds-label mb-1'>코멘트</div>
                            <p className='text-foreground border-border bg-muted/40 rounded-[var(--radius)] border p-3 text-sm whitespace-pre-wrap'>
                                {feedback.comment}
                            </p>
                        </div>
                    ) : null}

                    <div>
                        <div className='krds-label mb-1'>
                            평가된 답변 ({message.role})
                        </div>
                        <p className='text-foreground border-border bg-muted rounded-[var(--radius)] border p-3 text-sm whitespace-pre-wrap'>
                            {message.content}
                        </p>
                        <p className='text-muted-foreground mt-1 text-[11px]'>
                            {formatDateTime(message.createdAt)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}
